import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StepperContent, StepperHeader, StepperSubmit } from '../../../../../shared/general';
import {
  getDefaultValuesStep2,
  getMaximumMortgageByIncomeRequestBody
} from '../../utils/calculators-student-loan-utils';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { FormElementDynamicInputTyped } from '../../../../../shared/form-elements';
import {
  CalculatorsStudentLoanFormFields,
  CalculatorsStudentLoanResult,
  CalculatorsStudentLoanStep2FormFields
} from '../../../../../../@types/features/calculators';
import { calculatorStudentLoanStep2FormFields } from '../../config/calculator-student-loan-step-2-form-fields';
import { utilNumberToCurrency, utilStringToNumber } from '../../../../../../utils';
import { useFormContext } from '../../../../../../hooks';
import {
  ApiMaximumMortgageByIncomeRequest,
  ApiMaximumMortgageByIncomeResponse
} from '../../../../../../@types/shared/api';
import { API } from '../../../../../../services';
import SharedIncomeCalculator from '../../../../../shared/income-calculator/shared-income-calculator';
import { SharedIconCalc } from '../../../../../shared/icons';
import { validationMessages } from '../../../../../shared/validation/validation-messages';

export const CalculatorStudentLoanStepperStep2: FunctionComponent<{ id: number }> = ({ id }) => {
  const [interest, setInterest] = useState(0);
  const [completedText, setCompletedText] = useState('');
  const [incomeCalculatorField, setIncomeCalculatorField] =
    useState<keyof CalculatorsStudentLoanStep2FormFields>('applicantYearlyIncome');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setFocus,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: getDefaultValuesStep2()
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { updateForm, updateResult, setError, form } = useFormContext<
    CalculatorsStudentLoanResult,
    CalculatorsStudentLoanFormFields
  >();

  useEffect(() => {
    const fetchData = async () => {
      const res = await API.post<
        ApiMaximumMortgageByIncomeResponse,
        ApiMaximumMortgageByIncomeRequest
      >(
        'calculator/maximummortgagebyincome',
        getMaximumMortgageByIncomeRequestBody(
          {
            applicantYearlyIncome: '40000',
            partnerYearlyIncome: '0',
            applicantAge: '40',
            partnerAge: '0'
          },
          form as CalculatorsStudentLoanFormFields,
          interest
        )
      );

      setInterest(res.interest);
    };

    fetchData();
  }, []);

  const onSubmit = async (
    formData: Record<keyof CalculatorsStudentLoanStep2FormFields, string>
  ) => {
    updateForm(formData);

    const yearlyIncome =
      utilStringToNumber(formData.applicantYearlyIncome) +
      (form.withPartner === 'true' ? utilStringToNumber(formData.partnerYearlyIncome) : 0);

    const studentDebtAmount =
      utilStringToNumber(form.studentDebtAmount) +
      (form.withPartner === 'true' ? utilStringToNumber(form.partnerStudentDebtAmount) : 0);
    try {
      const res = await API.post<
        ApiMaximumMortgageByIncomeResponse,
        ApiMaximumMortgageByIncomeRequest
      >(
        'calculator/maximummortgagebyincome',
        getMaximumMortgageByIncomeRequestBody(
          formData,
          form as CalculatorsStudentLoanFormFields,
          interest
        )
      );

      updateResult({
        studentDebtAmount: utilNumberToCurrency(studentDebtAmount),
        withPartner: form.withPartner === 'true',
        yearlyIncome: utilNumberToCurrency(yearlyIncome),
        maxMortgageAmount: utilNumberToCurrency(res.maxMortgageAmount),
        monthlyPayment: utilNumberToCurrency(res.grossMonthlyCosts)
      });

      setCompletedText(utilNumberToCurrency(yearlyIncome, true, 0));
    } catch (e) {
      setError(true);
      // Handle error
    }
  };

  const openIncomeCalculator = (field: keyof CalculatorsStudentLoanStep2FormFields) => {
    setIncomeCalculatorField(field);
    onOpen();
  };

  const closeIncomeCalculator = (value: string) => {
    setValue(incomeCalculatorField, value);
    setFocus(incomeCalculatorField);
    onClose();
  };

  return (
    <Fragment>
      <StepperHeader title="Je bruto jaarinkomen" id={id} completedText={completedText} />
      <StepperContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={`grid grid-cols-1 gap-4`}>
            <div className={`flex`}>
              <FormElementDynamicInputTyped<CalculatorsStudentLoanStep2FormFields>
                formFields={calculatorStudentLoanStep2FormFields}
                id={'applicantYearlyIncome'}
                register={register}
                variant={`calculator`}
                errors={errors}
                values={watch()}
                validation={{
                  required: true,
                  min: { value: 1000, message: validationMessages.minimumIncome }
                }}
              />
              <div className={`mt-[40px]`}>
                <button
                  tabIndex={-1}
                  className={`rounded-full w-[5rem] h-[5rem] items-center flex justify-center ml-4 bg-gray-100`}
                  onClick={() => openIncomeCalculator('applicantYearlyIncome')}>
                  <SharedIconCalc boxSize={8}></SharedIconCalc>
                </button>
              </div>
            </div>

            <FormElementDynamicInputTyped<CalculatorsStudentLoanStep2FormFields>
              formFields={calculatorStudentLoanStep2FormFields}
              id={'applicantAge'}
              register={register}
              variant={`calculator`}
              errors={errors}
              validation={{
                required: true,
                min: { value: 18, message: validationMessages.minimumAge },
                max: { value: 152, message: validationMessages.max(152) }
              }}
              values={watch()}
            />

            {form.withPartner === 'true' && (
              <>
                <div className={`flex`}>
                  <FormElementDynamicInputTyped<CalculatorsStudentLoanStep2FormFields>
                    formFields={calculatorStudentLoanStep2FormFields}
                    id={'partnerYearlyIncome'}
                    register={register}
                    errors={errors}
                    values={watch()}
                    variant={`calculator`}
                    validation={{
                      required: true,
                      min: { value: 1000, message: validationMessages.minimumIncome }
                    }}
                  />
                  <div className={`mt-[40px]`}>
                    <button
                      className={`rounded-full w-[5rem] h-[5rem] items-center flex justify-center ml-4 bg-gray-100`}
                      onClick={() => openIncomeCalculator('partnerYearlyIncome')}>
                      <SharedIconCalc boxSize={8}></SharedIconCalc>
                    </button>
                  </div>
                </div>

                <FormElementDynamicInputTyped<CalculatorsStudentLoanStep2FormFields>
                  formFields={calculatorStudentLoanStep2FormFields}
                  id={'partnerAge'}
                  register={register}
                  errors={errors}
                  variant={`calculator`}
                  values={watch()}
                  validation={{
                    required: true,
                    min: { value: 18, message: validationMessages.minimumAge },
                    max: { value: 150, message: validationMessages.maximumAge }
                  }}
                />
              </>
            )}

            <StepperSubmit id={id} isValidated={isValid} />
          </div>
        </form>
      </StepperContent>

      <Modal isOpen={isOpen} onClose={onClose} size={`full`}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <SharedIncomeCalculator
              toggleIncomeCalculator={closeIncomeCalculator}></SharedIncomeCalculator>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Fragment>
  );
};
